import { Suspense, useLayoutEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';

import { Box, ColorsV2 } from '@electricjs/arc';

import PageSpinner from '@/components/PageSpinner';
import { EmbeddedErrorFallback } from './EmbeddedErrorFallback';
import { EmployeesMapGate } from './EmployeesMapGate';

const EMPLOYEES_LOAD_ERROR = new Error('Failed to load employees');
const reloadPage = () => window?.location.reload();

/**
 * Embedded Page Wrapper
 *
 * This wrapper is used for routes under /embed/* that are meant to be
 * embedded in iframes. It renders only the page content without any navigation
 * (no SideNav, TopNav, or banners).
 *
 * EmbeddedThemeProvider is applied higher up, in router.tsx's OutermostPageWrapper,
 * so it can also be applied to GlobalDialogs.
 */
export const EmbeddedPageWrapper = () => {
  const location = useLocation();
  const scrollableWrapperRef = useRef<HTMLElement>(null);

  // Reset scroll position when route changes
  useLayoutEffect(() => {
    if (scrollableWrapperRef.current) {
      scrollableWrapperRef.current.scrollTo({ top: 0 });
    }
  }, [location.pathname]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100vh"
      backgroundColor={ColorsV2.BACKGROUND}
      width="100vw"
      overflow="hidden">
      <Box flexGrow="1" overflowY="auto" ref={scrollableWrapperRef}>
        <ErrorBoundary
          FallbackComponent={EmbeddedErrorFallback}
          onReset={() => {
            window?.location.reload();
          }}>
          <Suspense fallback={<PageSpinner />}>
            <EmployeesMapGate
              errorFallback={
                <EmbeddedErrorFallback
                  error={EMPLOYEES_LOAD_ERROR}
                  resetErrorBoundary={reloadPage}
                />
              }>
              <Outlet />
            </EmployeesMapGate>
          </Suspense>
        </ErrorBoundary>
      </Box>
    </Box>
  );
};
